import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import {
  WAITLIST_BASE_COUNT,
  WAITLIST_BETA_CAP,
  WAITLIST_BETA_DATE,
  RECENT_SIGNUPS,
  daysUntil,
} from '@/seed/waitlist';
import WaitlistForm from './WaitlistForm';
import './waitlist.css';

/**
 * Waitlist (WAIT-01) — beta early-access conversion section.
 *
 * Inverted block treatment (bg-ink text-paper) to give it the strongest
 * visual weight on the page. Sits between Stats (social proof) and Footer
 * — the page asks for the conversion right after the trust frame lands.
 *
 * FOMO surface area:
 *  - Headline + 3-stat strip (queue / slots filled % / days-to-beta)
 *  - Recent-signup ticker scrolling across Balkan cities
 *  - Pill-shaped form with orange primary CTA
 *  - Privacy reassurance line below the form
 *
 * Phase 3 ships seed data; WAIT-02 (separate) will wire a real provider.
 */

const RECENT_LOOP = [...RECENT_SIGNUPS, ...RECENT_SIGNUPS, ...RECENT_SIGNUPS];

export default function Waitlist(): JSX.Element {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  const days = daysUntil(WAITLIST_BETA_DATE);
  const filledPct = Math.min(100, Math.round((WAITLIST_BASE_COUNT / WAITLIST_BETA_CAP) * 100));

  return (
    <section
      id="waitlist"
      className="border-ink bg-ink text-paper relative overflow-hidden border-b py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1240px] px-6">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={reduce ? { duration: 0 } : { duration: 0.5 }}
          className="text-center"
        >
          <div className="text-orange mb-7 inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.22em] uppercase">
            <span aria-hidden="true" className="bg-orange h-px w-6" />
            {t('waitlist.eyebrow')}
            <span aria-hidden="true" className="bg-orange h-px w-6" />
          </div>

          <h2 className="m-0 mb-6 font-sans text-[clamp(48px,7vw,96px)] leading-[0.95] font-semibold tracking-[-0.04em]">
            {t('waitlist.headlineLine1')}
            <span className="text-orange">{t('waitlist.headlineAccent')}</span>
            <br />
            {t('waitlist.headlineLine2')}
          </h2>

          <p className="text-paper/80 mx-auto mb-10 max-w-[600px] text-[17px] leading-[1.55]">
            {t('waitlist.subhead')}
          </p>

          <WaitlistForm />

          <p className="text-paper/70 mt-6 font-mono text-[11px] tracking-[0.18em] uppercase">
            {t('waitlist.privacy')}
          </p>
        </motion.div>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={reduce ? { duration: 0 } : { duration: 0.5, delay: 0.2 }}
          className="border-paper/15 mt-16 grid grid-cols-1 gap-8 border-t pt-10 sm:grid-cols-3"
        >
          <div className="text-center">
            <div className="text-orange font-mono text-[clamp(36px,4vw,52px)] leading-none font-bold tracking-[-0.02em]">
              {WAITLIST_BASE_COUNT.toLocaleString('en-US')}
            </div>
            <div className="text-paper/70 mt-2 font-mono text-[11px] tracking-[0.18em] uppercase">
              {t('waitlist.stats.queueLabel')}
            </div>
          </div>
          <div className="text-center">
            <div className="text-paper font-mono text-[clamp(36px,4vw,52px)] leading-none font-bold tracking-[-0.02em]">
              {filledPct}%
            </div>
            <div className="text-paper/70 mt-2 font-mono text-[11px] tracking-[0.18em] uppercase">
              {t('waitlist.stats.filledLabel', { cap: WAITLIST_BETA_CAP.toLocaleString('en-US') })}
            </div>
          </div>
          <div className="text-center">
            <div className="text-paper font-mono text-[clamp(36px,4vw,52px)] leading-none font-bold tracking-[-0.02em]">
              {days}
            </div>
            <div className="text-paper/70 mt-2 font-mono text-[11px] tracking-[0.18em] uppercase">
              {t('waitlist.stats.daysLabel')}
            </div>
          </div>
        </motion.div>

        <div aria-hidden="true" className="border-paper/15 mt-12 overflow-hidden border-t pt-6">
          <div className="text-paper/60 mb-3 text-center font-mono text-[10px] tracking-[0.22em] uppercase">
            {t('waitlist.recent.label')}
          </div>
          <div className="waitlist-ticker">
            <div className="waitlist-ticker-track">
              {RECENT_LOOP.map((idx, i) => (
                <span
                  key={`${idx}-${i}`}
                  className="text-paper/80 inline-flex items-center gap-3 px-6 font-mono text-[12px] tracking-[0.05em]"
                >
                  <span aria-hidden="true" className="rounded-pill bg-green h-1.5 w-1.5" />
                  {t(`waitlist.recent.entries.${idx}`)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
